import type { Client, StompSubscription } from '@stomp/stompjs';
import produce from 'immer';
import useObservable from 'packages/workspace/modules/helpers/hooks/useObservable';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';

export const ChatSessionStates = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
};

export interface IChatSession {
  connectionStatus: string;
  chat_client: Client | null;
  requested: boolean;
  connected: boolean;
  activeSubscriptions?: Array<StompSubscription>;
}

export const initialChatSession: IChatSession = {
  connectionStatus: ChatSessionStates.DISCONNECTED,
  chat_client: null,
  requested: false,
  connected: false,
};

const chatStoreSubject = new BehaviorSubject(initialChatSession);

export const chatStore = {
  requestChatSession: (): void => {
    const state = produce(chatStoreSubject.value, (draftState) => {
      draftState.requested = true;
    });
    chatStoreSubject.next(state);
  },
  setClient: (client: Client): void => {
    const state = produce(chatStoreSubject.value, (draftState) => {
      draftState.chat_client = client;
      draftState.connectionStatus = ChatSessionStates.CONNECTED;
      draftState.connected = true;
    });
    chatStoreSubject.next(state);
  },
  resetClient: (): void => {
    const state = produce(chatStoreSubject.value, (draftState) => {
      draftState.chat_client = null;
      draftState.connectionStatus = ChatSessionStates.DISCONNECTED;
      draftState.connected = false;
    });
    chatStoreSubject.next(state);
  },
  disconnectClient: (): void => {
    const state = produce(chatStoreSubject.value, (draftState) => {
      draftState.chat_client?.deactivate();
      return initialChatSession;
    });
    chatStoreSubject.next(state);
  },
  /**
   * this is only called in case of disconnected
   * @param status
   */
  setConnectionStatus: (status: string): void => {
    const state = produce(chatStoreSubject.value, (draftState) => {
      draftState.connectionStatus = status;
      draftState.connected = false;
    });
    chatStoreSubject.next(state);
  },
  /**
   * Generic function to subscribe to topics.
   */
  subscribeToTopic: (topicName: string, callBackFn: (...args: any) => void, subParams?: { id: string; ack: string }): StompSubscription | undefined => {
    if (!subParams && chatStoreSubject.value.chat_client?.connected) {
      return chatStoreSubject.value.chat_client?.subscribe(topicName, (message: any) => {
        const parsedMessage = JSON.parse(message.body);
        callBackFn(parsedMessage.data, parsedMessage.event, message);
      });
    }
    if (chatStoreSubject.value.chat_client?.connected) {
      return chatStoreSubject.value.chat_client?.subscribe(
        topicName,
        (message: any) => {
          const parsedMessage = JSON.parse(message.body);
          callBackFn(parsedMessage.data, parsedMessage.event, message);
          message.ack();
        },
        subParams
      );
    }
  },
};

/**
 * All Topics here.
 */
export const MessageTopicsConstants = {
  TASK_RELATED_GROUP_CHAT: 'internal-task-conversation',
  CURRENT_AVAILABLE_USERS: '/topic/internal-conversation/activity/',
  INTERNAL_CHAT: '/internal-conversations',
  SUPERVISORS_ONLY_CHAT: '/supervisors-only-chat',
  INTERNAL_CHAT_IS_TYPING: 'internal-conversation/is-typing',
  SUBSCRIBED_QUEUES: 'queues',
  USER_PERSONAL_QUEUE: '/user/queue/new-subscription',
  TASK_CONVERSATION_MESSAGE: '/webchat/task-conversation/', // this will still be used ONLY in sending
  SMS_CONVERSATION: '/sms/task-conversation/', // this will be used ONLY in sending
  WHATSAPP_CONVERSATION: '/whatsapp/task-conversation/',
  TASK_CONVERSATION_MESSAGE_IS_TYPING: '/task-conversation/is-typing/',
  INTERNAL_TASK_CONVERSATION_MESSAGE_IS_TYPING: '/internal-task-conversation/is-typing/',
  SWOOP_IN: '/user/queue/swoop',
  NEW_SUPERVISION_REQUEST: '/user/queue/supervision-request',
  TASK_PARTICIPANTS: '/topic/participants',
  TASK_UPDATES: '/topic/task-updates',
  OMNICHANNEL_ACTIVITY: '/user/queue/omnichannel-activity',
  AUTO_LOGOUT: '/user/queue/auto-logout',
  DISCORD_CONVERSATION: '/discord/task-conversation/',
  SIGNAL_CONVERSATION: '/signal/task-conversation/'
};

export const prepareChannelVirtualTopicForClient = (client_id: string, task_sid: string, medium: string) => `/queue/Consumer.${client_id}.VirtualTopic.${medium}.task-conversation.${task_sid}`;
export const prepareGroupChatVirtualTopicForClient = (client_id: string, task_sid: string) => `/queue/Consumer.${client_id}.VirtualTopic.internal-task-conversation.${task_sid}`;
/**
 * Hook Usage everywhere.
 */

export default function useChatSession(): IChatSession {
  return useObservable<IChatSession>(chatStoreSubject, chatStoreSubject.value);
}
